// src/context/CartContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getCart,
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart as clearBackendCart,
  mergeCart,
  getProductById,
} from '../services/api';
import { useAuth } from './AuthContext';
import useNotification from '../hooks/useNotification';
import { useToast } from '../components/Notifications/ToastContainer';
import { trackCartEvent } from '../utils/telemetry';
import { addToCartConfirmation } from '../constants/shippingRules';
import { formatSpeciesName } from '../utils/scientificName';
import { formatProductName } from '../utils/productName';

const CART_MERGE_FLAG_KEY = "cartMerged";

const CartContext = createContext();

/**
 * The slim variation object a GUEST cart keeps in localStorage.
 *
 * Every field here exists because something downstream reads it: the
 * thumbnail cascade (Cowork parity run 2026-08-11), and — S3-01.6,
 * Cowork re-check #3 — the PACKAGE fields, whose absence made an
 * anonymous cart compose its line from genus+species and show
 * "QADELETE testplant – Package" for a box with its own name.
 * `species_type` rides along for the plants-ship-with-animals advisory,
 * which reads it off this same object.
 */
//: Bumped whenever a line needs FIELDS an older snapshot did not store.
//: Review #3: v1 carts (written before S3-01.6) carry no `species_type`
//: and no package fields, so a pre-existing plant-only guest cart still
//: walked into the checkout 422 and a package line still titled itself
//: from the anchor species. Old lines are re-hydrated from the server on
//: load rather than silently trusted.
export const GUEST_CART_SNAPSHOT_VERSION = 2;

export const guestVariationSnapshot = (variation) => ({
  _v: GUEST_CART_SNAPSHOT_VERSION,
  id: variation.id,
  count: variation.count,
  latest_price: variation.latest_price,
  morph: variation.morph,
  variation_type: variation.variation_type,
  image: variation.image,
  cover_display_url: variation.cover_display_url,
  cover_thumb_url: variation.cover_thumb_url,
  image_url: variation.image_url,
  product_id: variation.product_id,
  genus: variation.genus,
  species: variation.species,
  locality: variation.locality,
  common_names: variation.common_names,
  is_package: variation.is_package,
  product_name: variation.product_name,
  package_summary: variation.package_summary,
  package_kind: variation.package_kind,
  species_type: variation.species_type,
});

/** True for a line written before the current snapshot shape. */
export const guestLineNeedsHydration = (line) => {
  const v = line && line.product_variation;
  if (!v) return false;
  return (v._v || 1) < GUEST_CART_SNAPSHOT_VERSION;
};

/**
 * Bring an old localStorage cart up to the current snapshot shape.
 *
 * `fetchProduct(productId)` returns the product payload (the public
 * product detail the storefront already uses); the variation is picked
 * out of it by id. A line whose product or variation is gone is DROPPED
 * — it cannot be bought and a stale line would fail at checkout instead
 * of here — and reported so the caller can tell the shopper.
 *
 * Never throws: a network failure leaves the line exactly as it was
 * (stale, but present), because losing a cart is worse than a stale
 * label.
 */
export const hydrateGuestCart = async (items, fetchProduct) => {
  const lines = Array.isArray(items) ? items : [];
  if (!lines.some(guestLineNeedsHydration)) {
    return { items: lines, dropped: [], changed: false };
  }
  const dropped = [];
  const hydrated = [];
  let changed = false;
  for (const line of lines) {
    if (!guestLineNeedsHydration(line)) {
      hydrated.push(line);
      continue;
    }
    const stale = line.product_variation || {};
    try {
      const product = await fetchProduct(stale.product_id);
      const fresh = (product?.variations || []).find((v) => v.id === stale.id);
      if (!fresh) {
        dropped.push(stale.id);
        changed = true;
        continue;
      }
      hydrated.push({
        ...line,
        product_variation: {
          ...guestVariationSnapshot({ ...stale, ...fresh }),
          // The shopper's own line total is theirs; the snapshot's
          // pricing fields are refreshed from the server above.
        },
      });
      changed = true;
    } catch (err) {
      console.warn('[CART] Could not refresh guest cart line',
        stale.id, err?.message || err);
      hydrated.push(line);
    }
  }
  return { items: hydrated, dropped, changed };
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, loading, hydrated } = useAuth();
  const { showNotification } = useNotification();
  const { showToast } = useToast();

  const loadLocalCart = () => {
    try {
      const stored = localStorage.getItem('cart');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('[CART] Error loading localStorage cart:', error);
      return [];
    }
  };

  // Initialize cartItems from localStorage immediately for anonymous users
  // This prevents "empty cart" flash before useEffect runs
  const initialAuthData = (() => {
    try {
      const stored = localStorage.getItem('auth');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  })();

  const isLikelyAnonymous = !initialAuthData || !initialAuthData.token;

  const [cartItems, setCartItems] = useState(() => {
    return isLikelyAnonymous ? loadLocalCart() : [];
  });
  const [cart, setCart] = useState(null);
  // For anonymous users with cart items already loaded, start with isLoading=false
  const [isLoading, setIsLoading] = useState(() => {
    if (isLikelyAnonymous) {
      const localCart = loadLocalCart();
      // If we have items in localStorage, we're ready to display immediately
      return localCart.length === 0;
    }
    return true; // Authenticated users need to fetch from backend
  });
  const [cartWasAdjusted, setCartWasAdjusted] = useState(false);
  const [cartMerged, setCartMerged] = useState(false);

  // Ref to coalesce concurrent ensureBackendCart calls
  const inFlightEnsureRef = React.useRef(null);

  // Ref to prevent concurrent fetchCart calls
  const isFetchingRef = React.useRef(false);

  // Debounce timer for cart refetch after mutations
  const refetchTimerRef = React.useRef(null);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const emailConfirmed = searchParams.get("confirmed") === "true";

  const saveLocalCart = (items) => {
    localStorage.setItem('cart', JSON.stringify(items));
  };

  const clearLocalCart = () => {
    localStorage.removeItem('cart');
  };

  const reloadLocalCart = () => {
    const localItems = loadLocalCart();
    setCartItems(localItems);
  };

  const fetchCart = async () => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      console.log("[CART][FETCH] ⏸️ Fetch already in progress, skipping duplicate call");
      return null;
    }

    isFetchingRef.current = true;
    console.log("[CART][FETCH] Starting fetchCart...");
    console.log("[CART][FETCH] getCart function:", typeof getCart, getCart);
    try {
      console.log("[CART][FETCH] About to call getCart() API...");
      const backendCart = await getCart();
      console.log("[CART][FETCH] ✅ API call successful! Full backend response:", backendCart);
      console.log("[CART][FETCH] Cart ID from backend:", backendCart.id);
      console.log("[CART][FETCH] Cart items count:", backendCart.items?.length || 0);
      console.log("[CART][FETCH] Backend cart object keys:", Object.keys(backendCart || {}));
      console.log("[CART][FETCH] Backend cart has .id field:", backendCart.hasOwnProperty('id'));

      setCartItems(backendCart.items || []);

      // ✅ Ensure cart object always has an ID when items exist
      if (backendCart && backendCart.items?.length > 0 && !backendCart.id) {
        console.log("[CART][FETCH] 🔧 Backend cart missing ID but has items, generating temporary ID...");
        backendCart.id = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }

      setCart(backendCart); // ✅ Save full cart object

      if (backendCart.message || backendCart.error_code) {
        console.log("[CART][FETCH] Cart metadata:", {
          message: backendCart.message,
          code: backendCart.error_code,
          items: backendCart.items?.map((item) => ({
            id: item?.id,
            variationId: item?.product_variation?.id,
            quantity: item?.quantity,
          }))
        });
      }

      if (backendCart.error_code === "QUANTITY_ADJUSTED") {
        showNotification("Some items in your cart were adjusted due to stock limits.", "warning");
        setCartWasAdjusted(true);
      }

      return backendCart; // Return the cart object
    } catch (error) {
      console.error('[CART][FETCH] ❌ API call failed:', error);
      console.error('[CART][FETCH] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return null; // Return null on error
    } finally {
      console.log("[CART][FETCH] Setting isLoading to false...");
      setIsLoading(false);
      isFetchingRef.current = false;  // Reset guard
    }
  };

  useEffect(() => {
    console.log("[CART][USEEFFECT] Auth state changed:", {
      loading,
      isAuthenticated,
      willFetchCart: !loading && isAuthenticated
    });
    
    if (!loading) {
      if (isAuthenticated) {
        console.log("[CART][USEEFFECT] Calling fetchCart for authenticated user...");
        fetchCart(); // backend version
      } else {
        console.log("[CART][USEEFFECT] Loading local cart for guest user...");
        const localItems = loadLocalCart();
        console.log("[DEBUG] Initial localStorage.cart at mount:", localItems);

        if (localItems.length === 0) {
          window.localStorage.setItem("requestCartSync", Date.now().toString());
        }

        setCartItems(localItems); // always populate memory from localStorage
        setIsLoading(false);

        // Review #3 — a cart written before the current snapshot shape
        // is missing fields the page now depends on (species_type for
        // the plants-ship-with-animals advisory, the package name for
        // the line title). Refresh those lines from the server once,
        // in the background: the cart is already on screen, so this only
        // ever corrects it.
        if (localItems.some(guestLineNeedsHydration)) {
          hydrateGuestCart(localItems, getProductById)
            .then(({ items: fresh, dropped, changed }) => {
              if (!changed) return;
              saveLocalCart(fresh);
              setCartItems(fresh);
              if (dropped.length) {
                console.warn('[CART] Dropped', dropped.length,
                  'cart line(s) that are no longer available:', dropped);
                showToast(
                  `${dropped.length} item${dropped.length === 1 ? '' : 's'} in your cart `
                  + 'is no longer available and was removed.',
                  'warning',
                );
              }
            })
            .catch((err) => console.warn(
              '[CART] Guest cart refresh failed:', err?.message || err));
        }
      }
    } else {
      console.log("[CART][USEEFFECT] Auth still loading, skipping cart fetch...");
    }
  }, [isAuthenticated, loading]);

  
  useEffect(() => {
    if (!isAuthenticated) {
      saveLocalCart(cartItems);
    }
  }, [cartItems, isAuthenticated]);

  useEffect(() => {
    const handleStorageChange = () => {
      const localItems = loadLocalCart();
      if (localItems.length === 0) {
        console.log("[CartContext] Ignored empty cart reload to avoid overwrite.");
        return;
      }
      console.log("[CartContext] Reloading cart from localStorage due to storage event or mount:", localItems);
      setCartItems(localItems);
    };
  
    if (!isAuthenticated) {
      window.addEventListener("storage", handleStorageChange);
      handleStorageChange(); // 🔥 runs once immediately
    }
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [isAuthenticated]);

  // Respond to cart sync requests from other tabs/windows
  useEffect(() => {
    const respondToCartSyncRequest = (e) => {
      if (e.key === "requestCartSync") {
        const storedCart = localStorage.getItem("cart");
        if (storedCart) {
          localStorage.setItem("cartSyncResponse", storedCart);
        }
      }
    };

    window.addEventListener("storage", respondToCartSyncRequest);
    return () => {
      window.removeEventListener("storage", respondToCartSyncRequest);
    };
  }, []);

  // If local cart is empty, request sync and poll for response
  useEffect(() => {
    let pollTimeout;

    const handleCartSyncResponse = (e) => {
      if (e.key === "cartSyncResponse") {
        try {
          const parsed = JSON.parse(e.newValue);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setCartItems(parsed);
            clearTimeout(pollTimeout);
          }
        } catch (err) {
          console.error("Failed to parse cartSyncResponse:", err);
        }
      }
    };

    const maybeStartPolling = () => {
      let attempts = 0;
      const maxAttempts = 20;

      const poll = () => {
        const response = localStorage.getItem("cartSyncResponse");
        if (response) {
          try {
            const parsed = JSON.parse(response);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setCartItems(parsed);
            }
          } catch (err) {
            console.error("Failed to parse cartSyncResponse during poll:", err);
          }
          return;
        }

        if (++attempts < maxAttempts) {
          pollTimeout = setTimeout(poll, 100);
        }
      };

      poll();
    };

    const initialItems = loadLocalCart();
    if (initialItems.length === 0) {
      window.localStorage.setItem("requestCartSync", Date.now().toString());
      maybeStartPolling();
    }

    window.addEventListener("storage", handleCartSyncResponse);
    return () => {
      clearTimeout(pollTimeout);
      window.removeEventListener("storage", handleCartSyncResponse);
    };
  }, []);

  const addItem = async (variation, quantity) => {
    console.log(`[CartContext] addItem() called — Auth: ${isAuthenticated ? 'Yes' : 'No'}`, {
      variationId: variation.id,
      quantity,
    });

    // Extract product name for toast message.
    // S3-01.6 (Cowork re-check #3): a PACKAGE goes by its own name — the
    // composition below names the ANCHOR species ("QADELETE testplant").
    const productName = (variation.is_package && variation.product_name
                          && formatProductName(variation, { name: variation.product_name })) ||
                        variation.morph?.name ||
                        variation.common_names?.[0] ||
                        (variation.genus && variation.species ? formatSpeciesName(variation, { withLocality: false }) : null) ||
                        'Animal';

    try {
      if (isAuthenticated) {
        const response = await addToCart(variation.id, quantity);

        setCartItems(response.items || []);

        // Show success toast. Batch 34 C5: a plant added to a cart that
        // holds no animal yet carries the ride-along sentence — once per
        // browser session (shippingRules.addToCartConfirmation).
        const done = addToCartConfirmation(quantity, productName, variation, response.items || []);
        showToast(done.message, done.type, done.duration);

        // Track cart event
        trackCartEvent('cart_item_added', response.id || cart?.id, {
          variation_id: variation.id,
          quantity,
          product_name: productName,
          unit_price: parseFloat(variation.latest_price?.price || 0),
        });

        // Ensure cart object is updated with full response (including cart id)
        if (response && !cart?.id) {
          console.log('[CART][ENSURE] addItem: triggering ensure', {
            auth: isAuthenticated,
            noCartId: !cart?.id,
            items: response.items?.length || 0
          });
          // Fetch full cart to get cart ID after first add
          const freshCart = await fetchCart();
          console.log('[CART][ENSURE] addItem: resolved cartId=', freshCart?.id || null);
        }
      } else {
        const existing = [...cartItems];
        const index = existing.findIndex((item) => item.product_variation.id === variation.id);

        const availableCount = variation.count;
        const currentQty = existing[index]?.quantity || 0;

        if (currentQty + quantity > availableCount) {
          showNotification(`Cannot exceed available stock. Only ${availableCount - currentQty} left.`, "error");
          return;
        }

        const unitPrice = parseFloat(variation.latest_price?.price || 0);

        if (index >= 0) {
          existing[index].quantity += quantity;
          existing[index].subtotal = existing[index].quantity * unitPrice;
        } else {
          existing.push({
            product_variation: guestVariationSnapshot(variation),
            quantity,
            subtotal: quantity * unitPrice,
          });
        }

        saveLocalCart(existing);
        console.log("[CartContext] Before setCartItems, current:", cartItems);
        console.log("[CartContext] New local cart:", existing);
        setCartItems([...existing]);  // ✅ Force state update
        console.log("[CartContext] After setCartItems — current cartItems state:", cartItems);
        console.log("[Cart][Anonymous] Updated local cart:", existing);

        // Show success toast (batch 34 C5: the ride-along sentence, once
        // per browser session, for a plant in a cart with no animal yet).
        const done = addToCartConfirmation(quantity, productName, variation, existing);
        showToast(done.message, done.type, done.duration);

        // Track cart event (anonymous user - no cart_id)
        trackCartEvent('cart_item_added', null, {
          variation_id: variation.id,
          quantity,
          product_name: productName,
          unit_price: unitPrice,
        });
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showToast(`Failed to add ${productName} to cart`, 'error');
    }
  };
  
  const updateItem = async (id, quantity) => {
    console.log('[CART] 🔄 qty change → updating item', { id, quantity });
    if (isAuthenticated) {
      // Update the item quantity
      await updateCartItem(id, quantity);
      console.log('[CART] ✅ qty update API call succeeded');

      // Track cart item updated event
      trackCartEvent('cart_item_updated', cart?.id, {
        cart_item_id: id,
        quantity,
      });

      // Debounce cart refetch to prevent rapid consecutive calls
      // Clear any pending refetch timer
      if (refetchTimerRef.current) {
        console.log('[CART] ⏱️ Clearing previous refetch timer');
        clearTimeout(refetchTimerRef.current);
      }

      // Schedule refetch after 250ms of no further updates
      console.log('[CART] ⏱️ Scheduling debounced cart refetch in 250ms...');
      refetchTimerRef.current = setTimeout(async () => {
        console.log('[CART] 📦 Debounce complete - refetching cart for updated discounts...');
        const freshCart = await fetchCart();

        if (freshCart) {
          console.log('[CART] 🎯 Cart refetched with fresh discount values:', {
            items_count: freshCart.items?.length,
            auto_discount: freshCart.auto_discount,
            promo_code_discount: freshCart.promo_code_discount,
            total_discount: freshCart.total_discount,
            subtotal: freshCart.subtotal,
            total: freshCart.total
          });
        }
        refetchTimerRef.current = null;
      }, 250);
    } else {
      const updatedItems = cartItems.map((item) => {
        if (item.product_variation.id === id) {
          const availableCount = item.product_variation.count;
          // Punch #77 (adv-review 2026-08-10, pre-existing): the anon
          // update changed `quantity` but never `subtotal`, and both the
          // line total and the summary PREFER item.subtotal — so a guest
          // stepping 2→3 saw qty 3 priced as 2, persisted to
          // localStorage, surviving reload. Recompute it with the
          // quantity. (Charges were always correct — login-merge recalcs
          // server-side — but the display lied indefinitely.)
          const unit = parseFloat(item.product_variation?.latest_price?.price || 0);
          const recompute = (qty) => (
            item.subtotal != null && unit > 0
              ? { subtotal: (unit * qty).toFixed(2) }
              : {}
          );
          if (quantity > availableCount) {
            showNotification(`Only ${availableCount} items available.`, "error");
            return { ...item, quantity: availableCount, ...recompute(availableCount) };
          }
          return { ...item, quantity, ...recompute(quantity) };
        }
        return item;
      });
      setCartItems(updatedItems);

      // Track cart item updated event (anonymous)
      trackCartEvent('cart_item_updated', null, {
        variation_id: id,
        quantity,
      });
    }
  };
  
  const removeItem = async (id) => {
    // Find the line: prefer the server row id (lot / pinned-animal lines
    // always carry one), fall back to variation id for local-only lines.
    const itemToRemove =
      cartItems.find((item) => item.id === id) ||
      cartItems.find((item) => item.product_variation?.id === id);

    const productName = (itemToRemove?.product_variation?.is_package
                          && itemToRemove?.product_variation?.product_name
                          && formatProductName(itemToRemove.product_variation,
                            { name: itemToRemove.product_variation.product_name })) ||
                        itemToRemove?.product_variation?.morph?.name ||
                        itemToRemove?.product_variation?.common_names?.[0] ||
                        (itemToRemove?.product_variation?.genus && itemToRemove?.product_variation?.species
                          ? formatSpeciesName(itemToRemove?.product_variation, { withLocality: false })
                          : null) ||
                        'Animal';

    // Cowork QA F2 (punch #54): lot-bundle and pinned-animal lines exist
    // ONLY in the BACKEND cart — LotListingCard/AnimalListingCard POST
    // straight to the API even for anonymous sessions, and fetchCart then
    // mirrors the server lines into local state. The old anonymous branch
    // filtered local state and never told the backend, so the session
    // cart kept the line and every refetch/merge resurrected it ("Remove
    // is dead", silently). Any line carrying a server row id must be
    // DELETEd server-side regardless of auth state — the session cookie
    // scopes the anonymous cart.
    const serverRowId = isAuthenticated ? (itemToRemove?.id || id) : (itemToRemove?.id || null);

    if (serverRowId) {
      const success = await removeFromCart(serverRowId);
      if (!success) {
        // F2: never a silent no-op — surface to the caller's catch/toast.
        throw new Error(`Cart item removal failed (id=${serverRowId})`);
      }
      trackCartEvent('cart_item_removed', cart?.id, {
        cart_item_id: serverRowId,
        product_name: productName,
      });
      if (isAuthenticated) {
        // Refetch cart to get updated discount calculations
        console.log('[CART] item removed → refetching cart for updated discounts');
        await fetchCart();
      } else {
        // Anonymous: drop exactly THIS line locally. Filtering by
        // variation id would also nuke a plain stock line sharing the
        // bundle's variation.
        setCartItems((items) => items.filter((item) => item.id !== serverRowId));
      }
      // Cowork run 7: a successful removal was painted in the ERROR
      // variant — right message, wrong clothes. It is a success.
      showToast(`Removed ${productName} from cart`, 'success');
      return;
    }

    // Local-only anonymous stock line — no backend row to delete.
    trackCartEvent('cart_item_removed', null, {
      variation_id: id,
      product_name: productName,
    });

    setCartItems((items) =>
      items.filter((item) => item.product_variation.id !== id)
    );
    // Cowork run 7: a successful removal was painted in the ERROR
      // variant — right message, wrong clothes. It is a success.
      showToast(`Removed ${productName} from cart`, 'success');
  };

  const mergeCartItems = async (promoCode = null) => {
    try {
      const localCart = loadLocalCart();

      const validItems = localCart.filter(
        (item) =>
          item?.product_variation?.id &&
          typeof item.quantity === "number" &&
          item.quantity > 0
      );

      if (!validItems.length) {
        console.log("[CartContext] No valid items in local cart to merge. Skipping clear.");
      }

      // CRITICAL FIX: Pass promo_code to backend merge endpoint
      const response = await mergeCart(validItems, promoCode);
      console.log("[CartContext] Cart merged:", response);

      if (response.adjustments?.length > 0) {
        showNotification("Some cart items were adjusted due to stock limits.", "warning");
        setCartWasAdjusted(true);
      }

      if (validItems.length > 0 && response?.items?.length) {
        console.log("[CartContext] Clearing local cart after successful merge and fetch.");
        clearLocalCart();
      }

      localStorage.setItem(CART_MERGE_FLAG_KEY, "true");
      return { adjusted: response.adjustments?.length > 0 };
    } catch (error) {
      console.error("Cart merge failed:", error);
      showNotification("Failed to merge carts after login.", "error");
      localStorage.removeItem(CART_MERGE_FLAG_KEY); // fallback safety
      return { adjusted: false };
    }
  };

  const clearCart = async () => {
    // Cowork QA F2: errors must PROPAGATE to the caller (CartPage toasts
    // them) — the old try/catch swallowed every failure into a stripped
    // console.log-level surface, so Clear Cart could fail with zero
    // feedback. Callers that must not fail (payment confirmation pages)
    // already wrap this in their own try/catch.
    const itemCount = cartItems.length;

    // Server-backed lines (lot bundles / pinned animals — and any line
    // mirrored from the backend session cart) live server-side even for
    // anonymous sessions; clearing only localStorage left them alive to
    // resurrect on the next fetch/merge.
    const hasServerLines = cartItems.some((item) => item?.id);

    if (isAuthenticated || hasServerLines) {
      const success = await clearBackendCart();
      if (!success) {
        throw new Error('Cart clear failed');
      }
    }

    trackCartEvent('cart_cleared', isAuthenticated ? cart?.id : null, {
      item_count: itemCount,
    });

    if (!isAuthenticated) {
      clearLocalCart();
    }
    setCartItems([]);
    localStorage.removeItem(CART_MERGE_FLAG_KEY);
  };

  const getItemQuantity = (variationId) => {
    const item = cartItems.find((i) => i.product_variation.id === variationId);
    return item ? item.quantity : 0;
  };

  const getTotalQuantity = () => {
    return cartItems.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getTotalQuantityForVariation = (variationId) => {
    const item = cartItems.find((i) => i.product_variation?.id === variationId);
    const qty = item?.quantity || 0;
    console.log(`Quantity in cart for variation ${variationId}:`, qty);
    return qty;
  };

  // Ensure cart exists after auth hydration if we have items but no cart ID
  const authHydrationEnsureAttemptedRef = React.useRef(false);

  useEffect(() => {
    const shouldEnsure =
      isAuthenticated &&
      hydrated &&
      cartItems.length > 0 &&
      !cart?.id &&
      !authHydrationEnsureAttemptedRef.current;

    if (!shouldEnsure) return;

    const doEnsure = async () => {
      authHydrationEnsureAttemptedRef.current = true;

      console.log('[CART][ENSURE] authHydrate: triggering ensure', {
        auth: isAuthenticated,
        hydrated,
        items: cartItems.length,
        noCartId: !cart?.id
      });

      await ensureBackendCart();
    };

    doEnsure();
  }, [isAuthenticated, hydrated, cartItems.length, cart?.id]);

  // Merge local cart after signup/login if needed
  const mergeAttemptedRef = React.useRef(false);

  useEffect(() => {
    const alreadyMerged = localStorage.getItem(CART_MERGE_FLAG_KEY) === "true";
    const localItems = loadLocalCart();
    const hasItems = localItems.length > 0;

    // CRITICAL FIX: Trigger merge on ANY authenticated state with local items, not just email confirmation
    // This covers: signup+email-verify, Google OAuth, AND regular login→cart flow
    const shouldMerge =
      isAuthenticated &&
      !loading &&
      hasItems &&
      !alreadyMerged &&
      !mergeAttemptedRef.current;

    if (!shouldMerge) {
      // Debug logging for merge skip conditions
      if (isAuthenticated && !loading && hasItems && alreadyMerged) {
        console.log('[CART][MERGE] Skipping merge - already merged (flag set)');
      } else if (isAuthenticated && !loading && hasItems && mergeAttemptedRef.current) {
        console.log('[CART][MERGE] Skipping merge - already attempted in this session');
      }
      return;
    }

    const doMerge = async () => {
      mergeAttemptedRef.current = true;

      console.log("[CART][MERGE] Authenticated with local cart items — initiating merge", {
        itemCount: localItems.length,
        alreadyMerged,
        pathname: location.pathname
      });

      // CRITICAL FIX: Include promo_code in merge request to persist through login
      let appliedPromoCode = null;
      try {
        appliedPromoCode = localStorage.getItem('appliedPromoCode');
        if (appliedPromoCode) {
          console.log('[CART][MERGE] Including promo code in merge:', appliedPromoCode);
        }
      } catch (error) {
        console.error('[CART][MERGE] Failed to read promo code from localStorage:', error);
      }

      const { adjusted } = await mergeCartItems(appliedPromoCode);

      if (adjusted) {
        showNotification("Some items in your cart were adjusted during account creation.", "warning");
      }

      await fetchCart(); // Always sync post-merge state
      console.log('[CART][MERGE] Merge complete, cart synced');

      // ✅ Only clear local cart if items were actually present
      const sentItems = loadLocalCart();
      if (sentItems.length > 0) {
        clearLocalCart();
        localStorage.setItem(CART_MERGE_FLAG_KEY, "true");
        console.log('[CART][MERGE] Local cart cleared, merge flag set');
      }
    };

    doMerge();
  }, [isAuthenticated, loading, cartItems]);

  const cartId = cart?.id || null;

  // Debug logging for cartId issues
  // #52 adv-review: for GUESTS items-without-cartId is the normal
  // localStorage state (this fired every render); for AUTHED users it is
  // a real broken-checkout precursor and deserves a prod-surviving warn.
  if (isAuthenticated && cartItems.length > 0 && !cartId) {
    console.warn("[CART][DEBUG] Have items but no cartId:", {
      cartItemsCount: cartItems.length,
      cart: cart,
      cartId: cartId,
      isAuthenticated,
      cartHasId: cart?.id ? 'YES' : 'NO',
      cartKeys: cart ? Object.keys(cart) : 'cart is null'
    });
  }

  // Ensure backend cart exists for authenticated users
  const ensureBackendCart = async () => {
    // If there's already an in-flight ensure, reuse it
    if (inFlightEnsureRef.current) {
      console.log('[CART][ENSURE] reusing in-flight promise');
      return inFlightEnsureRef.current;
    }

    console.log('[CART][ENSURE] start', {
      hasCartId: !!cart?.id,
      hasItems: cartItems.length > 0,
      isAuthenticated
    });

    // Early return if we already have a cart ID or don't need one
    if (!isAuthenticated || cart?.id || cartItems.length === 0) {
      console.log('[CART][ENSURE] early return, cartId=', cart?.id || null);
      return cart?.id || null;
    }

    // Create and store the in-flight promise
    inFlightEnsureRef.current = (async () => {
      try {
        console.log('[CART][ENSURE] No backend cartId but have items, triggering merge...');

        // Use existing merge logic to push local items to backend
        const localItems = loadLocalCart();
        if (localItems.length > 0) {
          await mergeCartItems();
          const freshCart = await fetchCart(); // Refresh to get cart ID from backend

          console.log('[CART][ENSURE] after merge+fetch, cartId=', freshCart?.id || null);
          return freshCart?.id || null;
        }

        console.log('[CART][ENSURE] no local items to merge');
        return null;
      } catch (error) {
        console.error('[CART][ENSURE] Failed to ensure cart:', error);
        return null;
      } finally {
        inFlightEnsureRef.current = null;
      }
    })();

    return inFlightEnsureRef.current;
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        cartId,
        cartItems,
        setCartItems,
        isLoading,
        refreshCart: fetchCart,
        mergeCartItems,
        ensureBackendCart,
        addItem,
        updateItem,
        removeItem,
        clearCart,
        getItemQuantity,
        getTotalQuantity,
        getTotalQuantityForVariation,
        cartWasAdjusted,
        setCartWasAdjusted,
        setCartMerged
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);